// AGENT-SPEC-EXPORT (docs/design/2026-10-31-consolidate-then-demo.md §2, A3; the diff of A12): what
// CompanyOS wants each container PROFILE to run, as `companyos.agent-spec/v2` documents
// (./agent-spec-v2.ts), for platform admins and the panel:
//
//   GET  /v1/agent-spec                                every profile's desired v2 document (?container=)
//   GET  /v1/agent-spec/{container}/{profile}          one profile's: the same document the bundle carries
//   POST /v1/agent-spec/{container}/{profile}/applied  the applier's report on that profile
//
// The container itself reads its profiles signed, on `GET /v1/agent-spec/{container}` (v2 bundle).
// The report lives under the profile (not at `/v1/agent-spec/applied`): that path is the v1
// container-token report until v1 is removed, and a v2 report is about one profile's version.
import { z } from "zod";
import { AgentProfileSpecReportStatus } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { AgentSpecContainerParams, AgentSpecFetchQuery, AgentSpecSignedBundle } from "./agent-spec";
import {
  AgentSpecV2Doc,
  AgentSpecV2Report,
  AgentSpecV2ReportAck,
  AgentSpecV2SignedBundle,
  ContainerName,
  ProfileName,
} from "./agent-spec-v2";
import { defineRoute } from "./route";

export const AGENT_PROFILE_SPEC_EXPORT_FORMAT = "companyos.agent-spec-export/v2";

/** One profile's desired v2 document as exported, and the applier's last report on it. */
export const AgentProfileSpecView = z
  .object({
    company_id: Uuid,
    container: z.string(),
    profile: z.string(),
    de_membership_id: Uuid.nullable(),
    /** The version CompanyOS wants this profile to run. */
    desired_version: z.int().min(1),
    /** SHA-256 of the canonical JSON of `spec` (sorted keys, no whitespace). */
    spec_sha256: z.string(),
    /**
     * SHA-256 of the comparable form (every tool list sorted, as a set): the hash an applier
     * compares, so a different order alone is never a config change.
     */
    compare_sha256: z.string(),
    note: z.string().nullable(),
    /** When the version was written (null only in an offline dump of the seed fixture). */
    created_at: Timestamp.nullable(),
    /** The applier's last report (null: none yet). */
    applied: z
      .object({
        applied_version: z.int().nullable(),
        last_status: AgentProfileSpecReportStatus,
        last_version: z.int(),
        last_reason: z.string().nullable(),
        reported_at: Timestamp,
      })
      .nullable(),
    spec: AgentSpecV2Doc,
  })
  .meta({ id: "AgentProfileSpecView" });

export const AgentProfileSpecList = z
  .object({
    format: z.literal(AGENT_PROFILE_SPEC_EXPORT_FORMAT),
    /** SHA-256 of the canonical JSON of `profiles` (each profile's identity, version and hash). */
    content_sha256: z.string(),
    profiles: z.array(AgentProfileSpecView),
  })
  .meta({ id: "AgentProfileSpecList" });

export const AgentProfileSpecParams = z.object({
  container: ContainerName,
  profile: ProfileName,
});
export const AgentProfileSpecListQuery = z.object({ container: ContainerName.optional() });

const operator = { feature: "core", tag: "agent-spec", access: "operator" } as const;

export const agentProfileSpecRoutes = {
  "agentSpec.fetch": defineRoute({
    feature: "core",
    tag: "agent-spec",
    access: "container_token",
    id: "agentSpec.fetch",
    method: "get",
    path: "/v1/agent-spec/{container}",
    summary:
      "The container's signed spec bundle, companyos.agent-spec/v2 (its own container token only; ?format=v1: the deprecated v1 bundle)",
    params: AgentSpecContainerParams,
    query: AgentSpecFetchQuery,
    response: { status: 200, schema: z.union([AgentSpecV2SignedBundle, AgentSpecSignedBundle]) },
    errors: [],
    emits: [],
  }),
  "agentSpec.profiles": defineRoute({
    ...operator,
    id: "agentSpec.profiles",
    method: "get",
    path: "/v1/agent-spec",
    summary:
      "Every container profile's desired agent spec, companyos.agent-spec/v2 (platform admins; key references only)",
    query: AgentProfileSpecListQuery,
    response: { status: 200, schema: AgentProfileSpecList },
    errors: [],
    emits: [],
  }),
  "agentSpec.profile": defineRoute({
    ...operator,
    id: "agentSpec.profile",
    method: "get",
    path: "/v1/agent-spec/{container}/{profile}",
    summary:
      "One container profile's companyos.agent-spec/v2 document, the one its signed bundle carries (platform admins)",
    params: AgentProfileSpecParams,
    response: { status: 200, schema: AgentProfileSpecView },
    errors: ["not_found"],
    emits: [],
  }),
  "agentSpec.profileApplied": defineRoute({
    ...operator,
    id: "agentSpec.profileApplied",
    method: "post",
    path: "/v1/agent-spec/{container}/{profile}/applied",
    summary:
      "The applier reports a profile's spec version: pending, applied, drift, shadow_planned or failed (409: not the desired version). The container's own token ({container} = its container id, its own profiles only; else 403) or a platform admin ({container} = the panel's container name)",
    params: AgentProfileSpecParams,
    body: AgentSpecV2Report,
    // A control-plane row (the profile's applied version and last report); no company event.
    safe: true,
    response: { status: 200, schema: AgentSpecV2ReportAck },
    errors: ["not_found", "conflict", "forbidden"],
    emits: [],
  }),
} as const;
